import React, { useEffect, useState } from 'react';
import type { Campaign, MarketingOverview } from '../marketingApi';

interface Props {
  campaign: Campaign;
  overview: MarketingOverview;
  onClose: () => void;
  onConfirm: (typedSubject: string) => Promise<void>;
}

/**
 * Two-step confirmation:
 *  1. Review who/what/when + tick that the test email was checked.
 *  2. Type the subject line exactly to unlock the final button.
 * The server re-checks everything (tested version, subject, content hash).
 */
const SendConfirmModal: React.FC<Props> = ({ campaign, overview, onClose, onConfirm }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [checkedTest, setCheckedTest] = useState(false);
  const [checkedAudience, setCheckedAudience] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const subjectMatches = typed.trim() === campaign.subject.trim();

  const submit = async () => {
    if (!subjectMatches || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(typed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Step {step} of 2</p>
            <h2 className="text-lg font-semibold text-gray-900">
              {step === 1 ? 'Review before sending' : 'Final confirmation'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-40"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {step === 1 ? (
          <div className="px-6 py-5 space-y-4">
            <dl className="rounded-lg border border-gray-200 divide-y divide-gray-200 text-sm">
              <div className="flex px-4 py-2.5">
                <dt className="w-28 shrink-0 text-gray-500">Recipients</dt>
                <dd className="font-semibold text-gray-900">
                  {overview.subscriber_count} subscribed user{overview.subscriber_count === 1 ? '' : 's'}
                </dd>
              </div>
              <div className="flex px-4 py-2.5">
                <dt className="w-28 shrink-0 text-gray-500">From</dt>
                <dd className="text-gray-900 break-all">{overview.from_email}</dd>
              </div>
              <div className="flex px-4 py-2.5">
                <dt className="w-28 shrink-0 text-gray-500">Subject</dt>
                <dd className="text-gray-900 break-words">{campaign.subject}</dd>
              </div>
              <div className="flex px-4 py-2.5">
                <dt className="w-28 shrink-0 text-gray-500">Goes out</dt>
                <dd className="text-gray-900">
                  {overview.send_delay_minutes} minutes after you confirm. You can cancel until then.
                </dd>
              </div>
            </dl>

            <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
                checked={checkedTest}
                onChange={(e) => setCheckedTest(e.target.checked)}
              />
              <span>
                I opened the test email sent to <strong>{campaign.tested_to}</strong> and checked the text,
                links and images.
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
                checked={checkedAudience}
                onChange={(e) => setCheckedAudience(e.target.checked)}
              />
              <span>I understand that once it goes out it can't be recalled.</span>
            </label>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back to editing
              </button>
              <button
                type="button"
                disabled={!checkedTest || !checkedAudience}
                onClick={() => setStep(2)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-gray-700">
              To confirm, type the subject line exactly:
            </p>
            <p className="text-sm font-mono bg-gray-50 border border-gray-200 rounded px-3 py-2 break-words select-none">
              {campaign.subject}
            </p>
            <input
              autoFocus
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onPaste={(e) => e.preventDefault()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
              placeholder="Type the subject here"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
            )}

            <div className="flex justify-between gap-3 pt-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => setStep(1)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
              >
                Back
              </button>
              <button
                type="button"
                disabled={!subjectMatches || busy}
                onClick={submit}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? 'Scheduling…' : `Send to ${overview.subscriber_count} subscribers`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendConfirmModal;
